/*
 * Service Worker Flow
 *
 * Deferred reload path (DEFAULT)
 *   SW detects new version → onNeedRefresh → emits 'acv::app-update-ready' →
 *   router.beforeEach picks it up on the user's next path navigation →
 *   emits 'acv::app-trigger-update' → updateApp() → updateServiceWorker() skips waiting →
 *   controllerchange fires → page reloads with new version already precached locally
 *   (instant, no network needed for JS/CSS).
 *
 * Updates are always deferred until the user's next route change
 *   SW detects new version → onNeedRefresh → emits 'acv::app-update-ready' →
 *   router.beforeEach triggers the update on the next navigation and reloads with the new version.
 *
 * Sad path (registration error):
 *   registerSW fails → onRegisterError
 */

import { mapGetters } from 'vuex';
import Analytics from '@/mixins/Analytics';
import eventBus from '@/utilities/EventBus';

export default {
  name: 'RegisterServiceWorker',
  data() {
    return {
      updateSW: undefined,
      offlineReady: false,
      needRefresh: false,
      pendingNavigationUrl: null,
    };
  },
  mixins: [Analytics],
  computed: {
    ...mapGetters({
      currentUser: 'currentUser',
    }),
  },
  async mounted() {
    try {
      await this.initializeServiceWorker();

      // Reload when new service worker takes control (but not on initial load)
      if ('serviceWorker' in navigator) {
        let refreshing = false;

        const hasControlCurrently = !!navigator.serviceWorker.controller;

        navigator.serviceWorker.addEventListener(
          'controllerchange',
          () => {
            // Prevent multiple reloads
            if (refreshing) return;

            // Only reload if we already had a controller (update)
            if (hasControlCurrently) {
              refreshing = true;
              this.track(this.eventNames.serviceWorker.reloadingAutomatically, {
                userId: this.currentUser.id,
                navigator,
                needsRefresh: this.needRefresh,
                isAuthorized: this.isAuthorized,
              });

              // final reload
              this.performSWControllerReload();
            }
          },
          { once: true },
        ); // fixes infinite reload loop

        // Deferred router guard signals us to trigger the update on the user's next navigation
        eventBus.$on('acv::app-trigger-update', (destinationUrl) => {
          this.pendingNavigationUrl = destinationUrl || null;
          this.track(this.eventNames.serviceWorker.forceReloadOnRouteChangeTriggered, {
            userId: this.currentUser.id,
            navigator,
            hasServiceWorker: 'serviceWorker' in navigator,
            isAuthorized: this.isAuthorized,
          });
          this.updateApp();
        });
      }
    } catch (e) {
      this.track(this.eventNames.serviceWorker.initializationFailed, {
        userId: this.currentUser.id,
        needsRefresh: this.needRefresh,
        errorMessage: e?.message || e,
        navigator,
        hasServiceWorker: 'serviceWorker' in navigator,
      });
    }
  },

  methods: {
    async initializeServiceWorker() {
      // eslint-disable-next-line import/no-unresolved
      const { registerSW } = await import('virtual:pwa-register');
      const vm = this;
      this.updateSW = registerSW({
        immediate: true,
        onOfflineReady() {
          vm.offlineReady = true;
        },
        onNeedRefresh() {
          vm.needRefresh = true;
          vm.handleNeedRefresh();
        },
        // no-op onNeedReload so the reload mechanism is handled in performSWControllerReload
        // after controller-change
        onNeedReload() {},
        onRegisteredSW(swUrl, swRegistration) {
          vm.handleSWManualUpdates(swUrl, swRegistration);
        },
        onRegisterError(e) {
          vm.handleSWRegisterError(e);
        },
      });
    },
    updateServiceWorker() {
      if (this.updateSW) {
        // skip waiting for controller change to happen
        this.track(this.eventNames.serviceWorker.skippedWaiting, {
          userId: this.currentUser.id,
          navigator,
        });
        this.updateSW();
      }
    },
    handleSWManualUpdates(swUrl, swRegistration) {
      const intervalMS = 60 * 60 * 1000; // hourly
      if (swRegistration) {
        setInterval(async () => {
          if (!(!swRegistration.installing && navigator)) {
            return;
          }

          if ('connection' in navigator && !navigator.onLine) {
            return;
          }

          const resp = await fetch(swUrl, {
            cache: 'no-store',
            headers: {
              cache: 'no-store',
              'cache-control': 'no-cache',
            },
          });

          if (resp?.status === 200) {
            await swRegistration.update();
          }
        }, intervalMS);
      }
    },
    handleNeedRefresh() {
      // Defer reload to the user's next path navigation — all new chunks are already
      // precached locally so the reload will be instant when it fires.
      eventBus.$emit('acv::app-update-ready');
    },
    updateApp() {
      // triggers skip waiting if possible
      this.track(this.eventNames.serviceWorker.existsToUpdateApp, {
        userId: this.currentUser.id,
        navigator,
        needsRefresh: this.needRefresh,
        hasServiceWorker: 'serviceWorker' in navigator,
      });

      if ('serviceWorker' in navigator) {
        this.updateServiceWorker();
        this.resetSwState();
      } else {
        window.location.reload(true);
      }
    },
    handleSWRegisterError(error) {
      this.track(this.eventNames.serviceWorker.registrationError, {
        userId: this.currentUser.id,
        navigator,
        hasServiceWorker: 'serviceWorker' in navigator,
        errorMessage: error?.message || error,
      });
    },
    performSWControllerReload() {
      // final reload after controller-change
      if (this.pendingNavigationUrl) {
        window.location.href = this.pendingNavigationUrl;
      } else {
        window.location.reload();
      }
    },
    resetSwState() {
      this.offlineReady = false;
      this.needRefresh = false;
    },
  },
};
